/* 
  Developed by: Jordyn Kao
  Contact: jordyninternational@gmail.com
  This component is part of the application and contains internal credits.
  This code is protected under the terms of the contract agreement.
  Unauthorized use or distribution of this code is prohibited.
*/
import React, { useState, useMemo } from 'react';
import { investmentData } from '@/data/investments';
import { Investment as InvestmentType } from '@/types/investment';
import { Button } from '@/components/Button';

import PageHero from '@/components/PageHero';
import InvestmentCard from '@/components/InvestmentCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const assetTypes = ['All', ...new Set(investmentData.map(i => i.type))];
const statuses = ['All', 'Open', 'Closed'];

const Investments: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');

  const filteredInvestments = useMemo(() => {
    return investmentData.filter(investment => {
      const matchesSearch = investment.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          investment.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = selectedType === 'All' || investment.type === selectedType;
      const matchesStatus = selectedStatus === 'All' || investment.status === selectedStatus;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [searchQuery, selectedType, selectedStatus]);

  return (
        <>
      <PageHero 
        title="Investment Opportunities"
        subtitle="Browse our curated selection of alternative investments"
        backgroundImage="https://images.unsplash.com/photo-1554260570-e9689a3418b8?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
      />
      
      <main className="flex-grow py-8">
        <div className="container mx-auto px-4">
          {/* Filter controls */}
          <div className="mb-8 px-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="w-full lg:w-auto lg:flex-1 lg:max-w-md">
              <input
                type="text"
                placeholder="Search by name or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--navy-dark))]"
              />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">Asset Class:</span>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="w-[180px] bg-white">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {assetTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">Status:</span>
                {statuses.map(status => (
                  <Button 
                    key={status} 
                    onClick={() => setSelectedStatus(status)} 
                    variant="ghost"
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${selectedStatus === status ? 'bg-navy-dark text-white' : 'text-gray-700 hover:bg-gray-200'}`}
                  >
                    {status}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Investment grid */}
          {filteredInvestments.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredInvestments.map((investment) => (
                <InvestmentCard key={investment.id} investment={investment} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-xl shadow-sm">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No investments found</h3>
              <p className="mt-1 text-gray-500">
                Try adjusting your search or filter to find what you're looking for.
              </p>
              <div className="mt-6">
                <Button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedType('All');
                    setSelectedStatus('All');
                  }}
                  variant="solid"
                  className="rounded-md shadow-sm"
                >
                  Reset all filters
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default Investments;
